import { useEffect, useRef } from 'react';
import type { WidgetConfig } from '../theme.js';
import { useWidgetData } from '../api.js';
import { EmptyState, ErrorState, Frame, Loading, UnavailableState } from '../chrome.js';
import { formatRate, formatStamp } from '../format.js';

/**
 * Forex rates grid.
 *
 * The upstream publishes a rate and nothing else — no bid/ask, no session open,
 * so there is no honest way to show a percentage change. What it can show is
 * movement since the last refresh, which is genuinely useful on a page someone
 * leaves open, and is derived here rather than invented by the server.
 */

interface FxQuote {
  pair: string;
  base: string;
  quote: string;
  rate: number;
  as_of: string;
}

const TITLE = 'Forex rates';

export function FxGrid({ config }: { config: WidgetConfig }) {
  const state = useWidgetData<FxQuote[]>(config, '/v1/fx');

  // Previous rates, so a refresh can be shown as a tick up or down. Held in a
  // ref rather than state: it must not itself trigger a render, and it must
  // survive one without being recomputed.
  const previous = useRef<Map<string, number>>(new Map());
  const rows = state.status === 'ready' ? state.envelope.data : [];

  useEffect(() => {
    if (rows.length === 0) return;
    // Deferred to a microtask after paint so the CURRENT render still sees the
    // old values to compare against.
    const snapshot = new Map(rows.map((r) => [r.pair, r.rate]));
    const timer = window.setTimeout(() => {
      previous.current = snapshot;
    }, 1200);
    return () => window.clearTimeout(timer);
  }, [rows]);

  if (state.status === 'loading') return <Loading title={TITLE} rows={7} />;
  if (state.status === 'error') return <ErrorState title={TITLE} message={state.message} />;
  if (state.status === 'unavailable')
    return (
      <UnavailableState
        title={TITLE}
        message={state.message}
        lastGoodAt={state.lastGoodAt}
        locale={config.locale}
        timeZone={config.timeZone}
      />
    );

  const { envelope, stale } = state;

  return (
    <Frame
      title={TITLE}
      stamp={envelope.as_of ? formatStamp(envelope.as_of, config.locale, config.timeZone) : undefined}
      stale={stale}
      attributions={envelope.attributions}
      locale={config.locale}
    >
      {envelope.data.length === 0 ? (
        <EmptyState message="No rates are available right now." />
      ) : (
        <table>
          <thead>
            <tr>
              <th scope="col">Pair</th>
              <th scope="col" className="num">
                Rate
              </th>
            </tr>
          </thead>
          <tbody>
            {envelope.data.map((row) => {
              const before = previous.current.get(row.pair);
              const direction =
                before === undefined || before === row.rate
                  ? null
                  : row.rate > before
                    ? 'up'
                    : 'down';

              return (
                <tr key={row.pair}>
                  <td className="sym">{row.pair}</td>
                  <td className={`num ${direction ?? ''}`}>
                    {formatRate(row.rate, row.quote, config.locale)}
                    {direction && (
                      <span aria-hidden="true"> {direction === 'up' ? '▲' : '▼'}</span>
                    )}
                    {direction && (
                      <span className="sr-only">
                        {direction === 'up' ? ' risen' : ' fallen'}
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </Frame>
  );
}
